import * as React from "react";
import { useSelector } from "react-redux";
import { Image } from "@bloomberg/media-ui-core";

import { DEFAULT_NATIVE_WIDTH } from "~constants";
import ModuleContext from "~components/modules/module/modulecontext";
import PlayIcon from "~components/shared/play-icon/PlayIcon";
import { isPrioritySelector } from "~redux/selectors";
import { type Image as LineupImage } from "~types/types";
import { makeSrcset, makeFallbackImage } from "~utils";
import { formatImageCaption } from "~utils/replaceCaption";
import stripMarkup from "~utils/stripMarkup";

import styles from "./styles.module.css";

interface LazyImageProps {
    altFallback?: string;
    aspectRatio?: number;
    contentType?: string;
    height?: number;
    image?: LineupImage;
    isLazy?: boolean;
    width?: number;
    className?: string;
    imageSizes?: string;
    showCaption?: boolean;
}

const DEFAULT_IMAGE_SIZES = `(min-width: 1px) and (max-width: 619px) 220px,
                        (min-width: 620px) and (max-width: 759px) 480px,
                        (min-width: 760px) and (max-width: 1019px) 640px, 1200px`;

export default function LazyImage({
    altFallback,
    aspectRatio,
    contentType,
    height,
    image,
    width,
    className,
    imageSizes = DEFAULT_IMAGE_SIZES,
    showCaption = false,
}: LazyImageProps) {
    const { baseUrl = "", alt, origWidth, credit } = image || {};

    const altText = alt || altFallback;

    const styleObject = {
        "--aspect-ratio": aspectRatio && Number(aspectRatio?.toFixed(2)),
    } as React.CSSProperties;

    let renderedWidth = width || origWidth || DEFAULT_NATIVE_WIDTH;
    let renderedHeight = height || -1;

    if (aspectRatio && height) {
        renderedWidth = Math.round(renderedHeight * aspectRatio);
        // Check that the ratio isn't pushing outside the max height
        // If it is, further crop the width
        if (origWidth && renderedWidth > origWidth) {
            renderedWidth = origWidth;
            renderedHeight = Math.round(renderedWidth / aspectRatio);
        }
    } else if (aspectRatio) {
        renderedHeight = Math.round(renderedWidth / aspectRatio);
    }

    const imageSrcSet = makeSrcset(baseUrl, renderedWidth, renderedHeight);

    const defaultSrc = makeFallbackImage(baseUrl);

    const replacedCaption = formatImageCaption(credit || "");

    const { componentName } = React.useContext(ModuleContext);
    const isPriority = useSelector((state) =>
        isPrioritySelector(state, componentName)
    );

    return (
        <figure data-component="lazy-image" className={className}>
            <div className={styles.lazy} style={styleObject}>
                {contentType === "relatedWithPlayIcon" && <PlayIcon />}
                <Image
                    altText={altText}
                    aspectRatio={aspectRatio}
                    src={defaultSrc}
                    srcSet={imageSrcSet}
                    height={height}
                    isLazy={!isPriority}
                    origWidth={origWidth}
                    width={width}
                    sizes={imageSizes}
                    fetchpriority={isPriority ? "high" : undefined}
                    useResponsivePNG
                />
            </div>
            {showCaption && Boolean(credit) && (
                <figcaption className={styles.caption}>
                    {stripMarkup(replacedCaption)}
                </figcaption>
            )}
        </figure>
    );
}
